import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import SearchAPI from '../../app/Search/SearchAPI';
import { fetchCollectionsResults } from '../../app/Search/actions/CollectionsActions';
import VideoSearchOptions from '../../app/Search/entities/VideoSearchOptions';
import { selectSearchIsLoading } from '../../app/Search/selectors/searchSelectors';
import { selectStockItems } from '../../app/Search/selectors/searchSelectors';
import { SiteEnum } from '../../common/SiteConstants/SiteConstants';
import type { StockItemDetails } from '../../common/types/StockItemTypes';
import { normalizeStockItems } from '../utils/contentUtils';

export interface ContentFetchingParams {
  category?: string;
  mediaType?: string;
  frameRates?: string[];
  searchTerm?: string;
  sort?: string;
  minQualityScore?: number;
  resolution?: string;
  galleryName?: string;
  numResults: string;
  contentMode: 'search' | 'collection' | 'mixed';
  customContent?: StockItemDetails[];
  builderIsEditing: boolean;
}

export const useVideoGridContentFetching = (params: ContentFetchingParams) => {
  const {
    category,
    mediaType,
    frameRates,
    searchTerm,
    sort,
    minQualityScore,
    resolution,
    galleryName,
    numResults,
    contentMode,
    customContent,
    builderIsEditing,
  } = params;

  const [content, setContent] = useState<StockItemDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tempSearchResults, setTempSearchResults] = useState<
    StockItemDetails[]
  >([]);

  const dispatch = useDispatch<any>();
  const reduxStockItems = useSelector(selectStockItems);
  const reduxLoading = useSelector(selectSearchIsLoading);

  const abortControllerRef = useRef<AbortController | null>(null);

  const parsedNumResults = parseInt(numResults, 10);
  const resultsToFetch = parsedNumResults;

  const clearLocalState = useCallback(() => {
    setContent([]);
    setLoading(true);
    setError(null);
    setTempSearchResults([]);
  }, []);

  const getSearchResultsCb = useCallback(async () => {
    if (contentMode !== 'search' && contentMode !== 'mixed') return [];

    try {
      const searchParameters =
        new VideoSearchOptions().buildSearchApiParameters({
          ...(category && { categories: category }),
          contentType: mediaType,
          frameRates,
          resultsPerPage: resultsToFetch,
          searchOrigin: builderIsEditing
            ? 'builder_editing'
            : 'builder-hook-generated',
          searchTerm,
          sort,
          minQualityScore,
          videoQuality: resolution,
        });
      const resp = await SearchAPI.searchBuilder(
        SiteEnum.Videoblocks,
        searchParameters,
        abortControllerRef.current?.signal
      );
      const json = await resp.json();
      const items =
        json?.data?.stockItems || json?.data?.items || json?.items || [];
      return normalizeStockItems(items);
    } catch (e) {
      setError('Failed to fetch search results');
      return customContent || [];
    }
  }, [
    category,
    mediaType,
    frameRates,
    resultsToFetch,
    searchTerm,
    sort,
    minQualityScore,
    resolution,
    contentMode,
    builderIsEditing,
    customContent,
  ]);

  const getCollectionResultsCb = useCallback(async () => {
    if (contentMode !== 'collection' && contentMode !== 'mixed') return;
    if (galleryName) {
      await dispatch(
        fetchCollectionsResults(
          galleryName,
          1,
          false,
          true,
          SiteEnum.Videoblocks
        )
      );
    }
  }, [galleryName, contentMode, dispatch]);

  const handleFetchContent = useDebouncedCallback(
    async () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);
      setTempSearchResults([]);

      try {
        if (contentMode === 'search') {
          const searchItems = await getSearchResultsCb();
          if (!abortControllerRef.current?.signal.aborted) {
            setContent(
              searchItems.length > 0 ? searchItems : customContent || []
            );
            setLoading(false);
          }
        } else if (contentMode === 'collection') {
          await getCollectionResultsCb();
        } else if (contentMode === 'mixed') {
          const searchPromise = getSearchResultsCb();
          const collectionPromise = galleryName
            ? getCollectionResultsCb()
            : Promise.resolve();

          const [searchDataFromPromise] = await Promise.all([
            searchPromise,
            collectionPromise,
          ]);
          if (!abortControllerRef.current?.signal.aborted) {
            setTempSearchResults(searchDataFromPromise || []);

            if (!galleryName) {
              setContent(searchDataFromPromise || []);
              setLoading(false);
            }
          }
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          // If aborted, there is a new request, so early return to keep loading state
          return;
        }
        if (!abortControllerRef.current?.signal.aborted) {
          setError('Failed to fetch some or all mixed results');
          setContent(customContent || []);
          setTempSearchResults([]);
          setLoading(false);
        }
      }
    },
    350,
    { maxWait: 1000 }
  );

  useEffect(() => {
    clearLocalState();
    handleFetchContent();
  }, [
    category,
    mediaType,
    frameRates,
    resolution,
    searchTerm,
    sort,
    minQualityScore,
    contentMode,
    galleryName,
    numResults,
    builderIsEditing,
  ]);

  useEffect(() => {
    if (!abortControllerRef.current) return;

    if (contentMode === 'collection') {
      const normalizedItems = normalizeStockItems(reduxStockItems || []);
      setContent(normalizedItems);
      setLoading(reduxLoading);
    } else if (contentMode === 'mixed') {
      const mixed: StockItemDetails[] = [];
      let i = 0,
        j = 0;
      const collectionItems = normalizeStockItems(reduxStockItems || []);

      const searchItemsForMixing = tempSearchResults;

      while (
        mixed.length < resultsToFetch &&
        (i < searchItemsForMixing.length || j < collectionItems.length)
      ) {
        if (i < searchItemsForMixing.length)
          mixed.push(searchItemsForMixing[i++]);
        if (mixed.length >= resultsToFetch) break;
        if (j < collectionItems.length) mixed.push(collectionItems[j++]);
      }

      setContent(mixed);
      setLoading(reduxLoading);
    }
  }, [
    reduxStockItems,
    reduxLoading,
    contentMode,
    resultsToFetch,
    tempSearchResults,
  ]);

  return { content, loading, error };
};
